/**
 * Fixture dashboards used by the demo tenant — render via DashboardRenderer.
 *
 * Phase-0 versions of the pack-shipped defaults referenced in
 * docs/06_iot_twin_alignment.md § 5 (operator-overview, melt-shop,
 * moulding-line, qc-summary, energy). The real JSON files land in
 * packs/foundry/dashboards/ in B0.10.
 */

import type { DashboardConfig } from "./types";

export const FX_DASHBOARD_PLANT_OVERVIEW: DashboardConfig = {
  id: "dash-plant-overview",
  name: "Plant overview",
  description: "Day-shift KPIs, active heats, alarm feed.",
  source: "my",
  owner: "you",
  updatedAt: "2026-05-24T09:12:00.000Z",
  cols: 12,
  rowHeight: 80,
  widgets: [
    {
      id: "w-kpi-temp",
      type: "kpi",
      title: "Bath temperature",
      subtitle: "Furnace 1 · current",
      layout: { x: 0, y: 0, w: 3, h: 3 },
      dataSource: { kind: "port", alias: "Furnace 1", keys: ["bath_temp"] },
    },
    {
      id: "w-kpi-power",
      type: "kpi",
      title: "Power",
      subtitle: "Furnace 1 · current",
      layout: { x: 3, y: 0, w: 3, h: 3 },
      dataSource: { kind: "port", alias: "Furnace 1", keys: ["power_kw"] },
    },
    {
      id: "w-kpi-moisture",
      type: "kpi",
      title: "Sand moisture",
      subtitle: "Sand mixer A",
      layout: { x: 6, y: 0, w: 3, h: 3 },
      dataSource: { kind: "port", alias: "Sand Mixer A", keys: ["moisture_content"] },
    },
    {
      id: "w-kpi-rejection",
      type: "kpi",
      title: "Rejection rate",
      subtitle: "Last 24 h",
      layout: { x: 9, y: 0, w: 3, h: 3 },
      dataSource: { kind: "port", alias: "Plant", keys: ["rejection_rate"] },
    },
    {
      id: "w-ts-bath",
      type: "timeseries",
      title: "Bath temperature trend",
      subtitle: "Furnace 1 · last 60 min",
      layout: { x: 0, y: 3, w: 8, h: 4 },
      dataSource: { kind: "port", alias: "Furnace 1", keys: ["bath_temp"] },
    },
    {
      id: "w-status",
      type: "status-grid",
      title: "Device status",
      layout: { x: 8, y: 3, w: 4, h: 4 },
    },
    {
      id: "w-events",
      type: "event-feed",
      title: "Recent activity",
      layout: { x: 0, y: 7, w: 5, h: 5 },
    },
    {
      id: "w-alarms",
      type: "alarm-list",
      title: "Active alarms",
      layout: { x: 5, y: 7, w: 7, h: 5 },
      config: { limit: 4 },
    },
  ],
};

export const FX_DASHBOARD_OPERATOR_OVERVIEW: DashboardConfig = {
  id: "dash-operator-overview",
  name: "Operator overview",
  description:
    "Standard shift dashboard for foundry operators — heat status, OEE, alarm feed.",
  source: "pack",
  pack: "foundry",
  owner: "SAHAS Labs",
  updatedAt: "2026-05-20T00:00:00.000Z",
  cols: 12,
  rowHeight: 80,
  widgets: [
    {
      id: "w-kpi-temp",
      type: "kpi",
      title: "Bath temperature",
      subtitle: "Furnace 1",
      layout: { x: 0, y: 0, w: 3, h: 3 },
      dataSource: { kind: "port", alias: "Furnace 1", keys: ["bath_temp"] },
    },
    {
      id: "w-kpi-temp-2",
      type: "kpi",
      title: "Bath temperature",
      subtitle: "Furnace 2",
      layout: { x: 3, y: 0, w: 3, h: 3 },
      dataSource: { kind: "port", alias: "Furnace 2", keys: ["bath_temp"] },
    },
    {
      id: "w-kpi-rejection",
      type: "kpi",
      title: "Rejection rate",
      subtitle: "Last shift",
      layout: { x: 6, y: 0, w: 3, h: 3 },
      dataSource: { kind: "port", alias: "Plant", keys: ["rejection_rate"] },
    },
    {
      id: "w-kpi-devices",
      type: "kpi",
      title: "Devices online",
      subtitle: "All sites",
      layout: { x: 9, y: 0, w: 3, h: 3 },
      dataSource: { kind: "tag", alias: "Plant", keys: ["devices_online"] },
    },
    {
      id: "w-pareto",
      type: "defect-pareto",
      title: "Defects · last 30 days",
      subtitle: "Sorted by frequency · cumulative % overlay",
      layout: { x: 0, y: 3, w: 7, h: 5 },
    },
    {
      id: "w-alarms",
      type: "alarm-list",
      title: "Active alarms",
      layout: { x: 7, y: 3, w: 5, h: 5 },
      config: { limit: 4 },
    },
    {
      id: "w-ts",
      type: "timeseries",
      title: "Furnace bath temperatures",
      subtitle: "Multi-furnace overlay · last 60 min",
      layout: { x: 0, y: 8, w: 12, h: 4 },
      dataSource: { kind: "port", alias: "All furnaces", keys: ["bath_temp"] },
      config: { variant: "line" },
    },
  ],
};

export const FX_DASHBOARD_MELT_SHOP: DashboardConfig = {
  id: "dash-melt-shop",
  name: "Melt shop",
  description: "Per-furnace heat timeline, bath temperature, power draw, energy per heat.",
  source: "pack",
  pack: "foundry",
  owner: "SAHAS Labs",
  updatedAt: "2026-05-20T00:00:00.000Z",
  cols: 12,
  rowHeight: 80,
  widgets: [
    {
      id: "w-kpi-1",
      type: "kpi",
      title: "Furnace 1 bath",
      layout: { x: 0, y: 0, w: 3, h: 3 },
      dataSource: { kind: "port", alias: "Furnace 1", keys: ["bath_temp"] },
    },
    {
      id: "w-kpi-2",
      type: "kpi",
      title: "Furnace 1 power",
      layout: { x: 3, y: 0, w: 3, h: 3 },
      dataSource: { kind: "port", alias: "Furnace 1", keys: ["power_kw"] },
    },
    {
      id: "w-kpi-3",
      type: "kpi",
      title: "Furnace 2 bath",
      layout: { x: 6, y: 0, w: 3, h: 3 },
      dataSource: { kind: "port", alias: "Furnace 2", keys: ["bath_temp"] },
    },
    {
      id: "w-kpi-4",
      type: "kpi",
      title: "Furnace 2 power",
      layout: { x: 9, y: 0, w: 3, h: 3 },
      dataSource: { kind: "port", alias: "Furnace 2", keys: ["power_kw"] },
    },
    {
      id: "w-ts-temps",
      type: "timeseries",
      title: "Bath temperature · both furnaces",
      layout: { x: 0, y: 3, w: 8, h: 5 },
      dataSource: { kind: "port", alias: "Both furnaces", keys: ["bath_temp"] },
    },
    {
      id: "w-table",
      type: "table",
      title: "Recent heats",
      layout: { x: 8, y: 3, w: 4, h: 5 },
    },
  ],
};

export const FX_DASHBOARD_QC_SUMMARY: DashboardConfig = {
  id: "dash-qc-summary",
  name: "QC summary",
  description: "Defect Pareto, mechanical test results, chemistry trends.",
  source: "pack",
  pack: "foundry",
  owner: "SAHAS Labs",
  updatedAt: "2026-05-20T00:00:00.000Z",
  cols: 12,
  rowHeight: 80,
  widgets: [
    {
      id: "w-pareto",
      type: "defect-pareto",
      title: "Defect Pareto",
      layout: { x: 0, y: 0, w: 7, h: 5 },
    },
    {
      id: "w-correlations",
      type: "correlation-heatmap",
      title: "Process × quality correlations",
      layout: { x: 7, y: 0, w: 5, h: 7 },
    },
    {
      id: "w-recent",
      type: "table",
      title: "Recent heats",
      layout: { x: 0, y: 5, w: 7, h: 4 },
    },
  ],
};

export const ALL_FIXTURE_DASHBOARDS: DashboardConfig[] = [
  FX_DASHBOARD_PLANT_OVERVIEW,
  FX_DASHBOARD_OPERATOR_OVERVIEW,
  FX_DASHBOARD_MELT_SHOP,
  FX_DASHBOARD_QC_SUMMARY,
];
